/* eslint-disable max-lines-per-function */
/* eslint-disable array-element-newline */
/* eslint-disable eqeqeq */
import qs from 'qs';

/**
 * @type {import('custom-extensions/search/lib/api/types').SearchRequest}
 */
const DEFAULT_PARAMS = {};

/**
 * @implements {import('custom-extensions/search/lib/api/types').SearchRequest}
 */
export class SearchState {

	/**
	 * Outputs the entire search state as a single object
	 */
	get output () {
		return {
			...this.params,
			filters: this.filters,
			sorting: this.sorting
		};
	}

	/**
	 * Converts the current state object into a query string object
	 */
	get queryStringObject () {
		return {
			...this.params,
			...this._queryStringFilters(),
			...this._queryStringSorting()
		};
	}

	/**
	 * Converts the current state object into a query string
	 * @type {string}
	 */
	get queryString () {
		return qs.stringify(
			this.queryStringObject,
			{
				arrayFormat: 'repeat'
			}
		);
	}

	/**
	 * @param {import('custom-extensions/search/lib/api/types').SearchRequest} defaultSearchParams
	 */
	constructor (defaultSearchParams = {}) {
		this.defaultSearchParams = {
			...DEFAULT_PARAMS,
			...defaultSearchParams
		};

		/**
		 * @type {import('custom-extensions/search/lib/api/types').SearchRequest}
		 */
		this.params = {...this.defaultSearchParams};

		/**
		 * @type {import('custom-extensions/search/lib/api/types').SearchRequestFilter[]}
		 */
		this.filters = [];

		/**
		 * @type {import('custom-extensions/search/lib/api/types').SearchRequestSorting}
		 */
		this.sorting = {};

		this.lockedState = {};
	}

	/**
	 * Convert the filters array into a queryString safe format
	 */
	_queryStringFilters () {
		return this.filters.reduce((output, filter) => {
			const type = filter.background
				? 'bgfilter'
				: 'filter';

			const keyRoot = `${type}.${filter.field}`;

			if (Array.isArray(filter.value)) {
				const lowKey = `${keyRoot}.low`;
				const highKey = `${keyRoot}.high`;

				return {
					...output,
					...filter.value[0] != void 0 && {[lowKey]: Array.isArray(output[lowKey]) ? output[lowKey].concat([filter.value[0]]) : [filter.value[0]]},
					...filter.value[1] != void 0 && {[highKey]: Array.isArray(output[highKey]) ? output[highKey].concat([filter.value[1]]) : [filter.value[1]]}
				};
			}

			output[keyRoot] = output[keyRoot] || [];
			output[keyRoot].push(filter.value);

			return output;
		}, {});
	}

	/**
	 * Convert the sorting object into a queryString safe format
	 */
	_queryStringSorting () {
		if (this.sorting.field && this.sorting.direction) {
			return {
				[`sort.${this.sorting.field}`]: this.sorting.direction
			};
		}

		return {};
	}

	_compareFilterRanges (range1, range2) {
		return range1[0] === range2[0] && range1[1] === range2[1];
	}

	_compareFilterValues (value1, value2) {
		const arrayCheck1 = Array.isArray(value1);
		const arrayCheck2 = Array.isArray(value2);

		if (arrayCheck1 && arrayCheck2) {
			return this._compareFilterRanges(value1, value2);
		}
		else if (arrayCheck1 || arrayCheck2) {
			return false;
		}

		return value1 === value2;
	}

	/**
	 * Reverse a queryString or queryStringObject into a SearchState compatible format
	 *
	 * @param {string | any} queryStringOrObject
	 */
	setStateFromQueryStringOrObject (queryStringOrObject) {
		// Generate state (queryString format) object from queryString
		const newState = typeof queryStringOrObject === 'string'
			? qs.parse(queryStringOrObject)
			: queryStringOrObject;

		const dynamicSetTests = [
			{
				fn: 'sort',
				match: 1,
				pattern: /^sort\.(.*)$/
			},
			{
				background: false,
				fn: 'toggleFilter',
				match: 1,
				pattern: /^filter\.([^.]+)$/
			},
			{
				background: false,
				fn: 'toggleFilter',
				pattern: /^filter\.(.*)\.(low|high)$/,
				type: 'rangeFilter'
			},
			{
				background: true,
				fn: 'toggleFilter',
				match: 1,
				pattern: /^bgfilter\.(.*)$/
			}
		];

		const rangeFilterMap = new Map();

		for (let key in newState) {
			const value = newState[key];

			switch (key) {
				case 'q': {
					this.query(value, false);
					break;
				}
				case 'resultsPerPage': {
					this.perPage(value, false);
					break;
				}
				default: {
					const testPassed = dynamicSetTests.some(test => {
						const match = key.match(test.pattern);

						if (match) {
							if (test.type === 'rangeFilter') {
								const [, field, rangeKey] = match;

								const found = {
									...rangeFilterMap.get(field) ?? {},
									[rangeKey]: value
								};

								if (found.low && found.high) {
									this[test.fn](field, [parseFloat(found.low), parseFloat(found.high)]);
								}
								else {
									rangeFilterMap.set(field, found);
								}
							}
							else if (value !== void 0 && value !== null) {
								if (Array.isArray(value)) {
									for (let arrayValue of value) {
										this[test.fn](match[test.match], arrayValue, test.background);
									}
								}
								else {
									this[test.fn](match[test.match], value, test.background);
								}
							}

							return true;
						}

						return false;
					});

					if (!testPassed) {
						this.other(key, value);
					}

					break;
				}
			}
		}

		return this;
	}

	/**
	 * @param {string} field
	 * @param {string | number[]} value
	 * @param {boolean} background
	 */
	addFilter (field, value, background = false) {

		/**
		 * @type {import('custom-extensions/search/lib/api/types').SearchRequestFilter}
		 */
		const filter = {
			background,
			field,
			value
		};

		this.filters.push(filter);

		return this;
	}

	/**
	 * @param {string} field
	 * @param {string | number[]} value
	 * @param {boolean} background
	 */
	removeFilter (field, value, background = false) {
		this.filters = this.filters.filter(filter => {
			return !(
				filter.field === field &&
				filter.background === background &&
				this._compareFilterValues(filter.value, value)
			);
		});

		return this;
	}

	/**
	 * @param {string} field
	 * @param {string | number[]} value
	 * @param {boolean} background
	 */
	toggleFilter (field, value, background = false) {
		const foundFilter = this.filters.find(filter => {
			return (
				filter.field === field &&
				filter.background === background &&
				this._compareFilterValues(filter.value, value)
			);
		});

		if (foundFilter) {
			return this.removeFilter(field, value, background);
		}

		return this.addFilter(field, value, background);
	}

	/**
	 * @param {string} category
	 */
	category (category) {
		this.params.category = category;

		return this;
	}

	/**
	 * @param {string} searchQuery
	 */
	query (searchQuery) {
		this.params.q = searchQuery;

		return this;
	}

	/**
	 * @param {number} resultsPerPage
	 */
	perPage (resultsPerPage) {
		this.params.resultsPerPage = resultsPerPage;

		return this;
	}

	/**
	 * @param {number} pageNumber
	 */
	page (pageNumber) {
		this.params.page = pageNumber;

		return this;
	}

	/**
	 * @param {string} field
	 * @param {'asc' | 'desc'} direction
	 */
	sort (field, direction = 'asc') {
		this.sorting = {
			direction,
			field
		};

		return this;
	}

	lock () {
		this.lockedState = structuredClone({
			filters: this.filters,
			params: this.params,
			sorting: this.sorting
		});

		return this;
	}

	reset () {
		this.params = this.lockedState.params ? {...this.lockedState.params} : {...this.defaultParams};
		this.filters = this.lockedState.filters ? [...this.lockedState.filters] : [];
		this.sorting = this.lockedState.sorting ? {...this.lockedState.sorting} : {};

		return this;
	}

	clearFacets () {
		// Clear all non-background filters from array
		this.filters = this.filters.filter(filter => {
			return filter.background;
		});

		return this;
	}

	/**
	 * @param {string} field
	 */
	clearFacet (field) {
		// Remove the filter matching the passed field from the array
		this.filters = this.filters.filter(filter => {
			return filter.field !== field;
		});

		return this;
	}

	/**
	 * @param {string} key
	 * @param {any} value
	 */
	other (key, value) {
		this.params[key] = value;

		return this;
	}
};
